import {Component, EventEmitter, Input, Output, ViewChild}  from '@angular/core';
import {NgbTypeaheadSelectItemEvent}                        from "@ng-bootstrap/ng-bootstrap";

import {TeamService}                                        from "../../services/team/team.service";
import {Team}                                               from "../../services/team/team";

import {Observable}                                         from "rxjs/Observable";
import {_do}                                                from "rxjs/operator/do";
import {switchMap}                                          from "rxjs/operator/switchMap";
import {distinctUntilChanged}                               from "rxjs/operator/distinctUntilChanged";
import {debounceTime}                                       from "rxjs/operator/debounceTime";
import {_catch}                                             from "rxjs/operator/catch";
import {isNullOrUndefined}                                  from "util";



@Component({

	selector: 'team-typeahead',
	providers: [TeamService],
	template: `
        <div [class.has-danger]="searchFailed">
            
	        <label class="font-weight-bold" for="team-typeahead-http">Team  [
                <span *ngIf="! searching">*</span>
                <span *ngIf="searching">
                    <img src="/assets/typeahead.gif" style="width: 16px; margin-top: 2px; cursor: default;" />
                </span>
                ]</label>
            
	        <input #typeaheadTeamInput
			       id="team-typeahead-http"
                   type="text"
                   class="form-control"
                   [attr.tabindex]="setTabIndex"
                   [required]="setRequired"
                   [ngClass]="setSize()"
                   [ngbTypeahead]="teamSearch"
                   placeholder="Search by team name"
                   [resultFormatter]="titleFormatter"
                   [inputFormatter]="titleFormatter"
                   (selectItem)="teamSelectedEvent($event)"
				   (keyup.enter)="enterPressedEvent()"
            />
            
            <div class="form-control-feedback" *ngIf="searchFailed">Team information unavailable</div>
	        
        </div>`,

})


/**
 * Encapsulate all the programming needed to run the
 * ng-bootstrap Typeahead control to retrieve a
 * Team object.  This is meant to be embedded in
 * an admin page.
 *
 */
export class TeamTypeaheadComponent {

	// These are semaphores used to hide or display messages.
	searchFailed = false;
	searching = false;


	/**
	 * Initialize the service that retrieves Team information.
	 *
	 * @param teamService  A TeamService object.
	 */
	constructor(

		private teamService: TeamService

	) {}


	@Input()
	public code:string;  // The League code ('NFL', 'NBA', etc.)  This comes in from the parent page.


	@Input()
	public setRequired:boolean = false;  // Whether this is a required field or not.  This comes in from the parent page.


	@Input()
	public setTabIndex:number;  // The tab index for the host page.  This comes in from the parent page.


	@Input()
	public size:string;  // The size of the components.  This comes in from the parent page.


	/**
	 * Controls the Team choices INPUT.
	 */
	@ViewChild('typeaheadTeamInput')
	typeaheadTeamInput: any;


	@Output()
	public onTeamChange = new EventEmitter<Team>();  // This notifies the parent page a Team has been selected.

	@Output() 
	public onEnterPressed = new EventEmitter<any>(); // This notifies the parent page of an Enter key press

	/**
	 * Read the size variable passed in from the hosting template
	 * and set the controls based on that.  Also read the length of
	 * the INPUT field and the 'required' field and add that information
	 * to the class, too.
	 *
	 * For the size, if size is null, '' is returned.  The other choices are
	 * 'sm' and 'lg' versions of 'form-control'.
	 *
	 * For the INPUT field length, return 'ng-invalid' if the field length
	 * is zero.
	 *
	 * @returns {string}  The appropriate Bootstrap value for the form type.
	 */
	setSize(): string {

		let attribute:string = '';

		// Handle the size stuff.
		if (isNullOrUndefined(this.size)) {
			attribute = '';
		} else if (this.size.toLowerCase().startsWith('s')) {
			attribute = 'form-control-sm';
		} else if (this.size.toLowerCase().startsWith('l')) {
			attribute = 'form-control-lg';
		}

		// Handle the INPUT invalid status.
		if (! this.setRequired) {
			return attribute;
		} else if (isNullOrUndefined(this.typeaheadTeamInput.nativeElement.value) || 0 === this.typeaheadTeamInput.nativeElement.value.length) {
			return attribute + ' ng-invalid'.trim();
		} else {
			return attribute;
		}

	}


	/**
	 * Used only when the parent page wants to set the
	 * INPUT field because it got the team name from
	 * some other source than a search.
	 *
	 * @param team  The name of the team to put into the INPUT box.
	 */
	setTypeaheadInput(team: string) {

		this.typeaheadTeamInput.nativeElement.value = team;

	}


	/**
	 * Used by the typeahead to get the team 'title' field
	 * so it can be placed into the typeahead INPUT field when
	 * the choice is made.
	 *
	 * @param result  The Team object selected by the user.
	 */
	titleFormatter = (result: Team) => result.title;


	/**
	 * The typeahead puts the search term into the
	 * TeamService and processes the results.  It also
	 * sets the semaphores to show/hide typeahead
	 * progress.
	 *
	 * @return The Team Observable that will return data or errors.
	 */
	teamSearch = (team: Observable<Team[]>) =>

		_do.call(

			switchMap.call(

				_do.call(
					distinctUntilChanged.call(
						debounceTime.call(team, 300)),
					() => this.searching = true
				),

				(term: string) =>
					_catch.call(
						_do.call(
							this.teamService.search(term, this.code),
							(teams: Team[]) => this.searchFailed = 0 === teams.length
						),

						() => {
							this.searchFailed = true;
							return Observable.of.call([]);
						}
					)
			),

			() => this.searching = false

		);


	/**
	 * The typeahead calls this method when the user makes
	 * a selection.  This notifies the parent page with an
	 * event that passes the data.
	 *
	 * @param selection  The event that holds the Team choice made by the user.
	 */
	teamSelectedEvent(selection: NgbTypeaheadSelectItemEvent): void {

		this.onTeamChange.emit(selection.item);

	}

	/**
	 * The typeahead calls this method whenever the Enter key is pressed
	 */
	enterPressedEvent(): void {
		this.onEnterPressed.emit();
	}
}



// WEBPACK FOOTER //
// ./src/app/typeaheads/team/team-typeahead.component.ts